<template>
    <div class="fb-config">
        <div v-if="!field" class="fb-config-empty">
            <div class="fb-config-empty-icon">
                <i class="fal fa-hand-pointer"></i>
            </div>
            <p class="fb-config-empty-text">{{ $t('form_builder.config.select_field') }}</p>
        </div>

        <template v-else>
            <div class="fb-config-header">
                <h3 class="fb-config-title">
                    <i :class="getFieldIcon()" class="mr-2 text-blue-500"></i>
                    {{ getFieldTypeName() }}
                </h3>
            </div>

            <div class="fb-config-body">
                <!-- Language tabs -->
                <div class="fb-config-tabs">
                    <button
                        v-for="lang in langs"
                        :key="lang"
                        class="fb-config-tab"
                        :class="{ active: activeLang === lang }"
                        @click="activeLang = lang"
                    >
                        {{ lang.toUpperCase() }}
                    </button>
                </div>

                <!-- Label -->
                <div class="fb-config-field">
                    <label>{{ $t('form_builder.config.label') }}</label>
                    <input
                        type="text"
                        :value="getTranslation('label')"
                        @input="setTranslation('label', $event.target.value)"
                        :placeholder="$t('form_builder.config.label')"
                    />
                </div>

                <!-- Instruction -->
                <div class="fb-config-field">
                    <label>{{ $t('form_builder.config.instruction') }}</label>
                    <input
                        type="text"
                        :value="getTranslation('instruction')"
                        @input="setTranslation('instruction', $event.target.value)"
                        :placeholder="$t('form_builder.config.instruction')"
                    />
                </div>

                <!-- Description -->
                <div class="fb-config-field">
                    <label>{{ $t('form_builder.config.description') }}</label>
                    <textarea
                        :value="getTranslation('description')"
                        @input="setTranslation('description', $event.target.value)"
                        :placeholder="$t('form_builder.config.description')"
                        rows="2"
                    ></textarea>
                </div>

                <!-- Help -->
                <div class="fb-config-field">
                    <label>{{ $t('form_builder.config.help') }}</label>
                    <input
                        type="text"
                        :value="getTranslation('help')"
                        @input="setTranslation('help', $event.target.value)"
                        :placeholder="$t('form_builder.config.help')"
                    />
                </div>

                <hr class="fb-config-divider" />

                <!-- Options section -->
                <h4 class="fb-config-section-title">{{ $t('form_builder.config.options_title') }}</h4>

                <!-- Required public -->
                <div class="fb-config-toggle">
                    <label>{{ $t('form_builder.config.required_public') }}</label>
                    <input type="checkbox" v-model="field.requiredPublic" :true-value="1" :false-value="0" />
                </div>

                <!-- Required backoffice -->
                <div class="fb-config-toggle">
                    <label>{{ $t('form_builder.config.required_backoffice') }}</label>
                    <input type="checkbox" v-model="field.requiredBackoffice" :true-value="1" :false-value="0" />
                </div>

                <!-- Hidden -->
                <div class="fb-config-toggle">
                    <label>{{ $t('form_builder.config.hidden') }}</label>
                    <input type="checkbox" v-model="field.hidden" :true-value="1" :false-value="0" />
                </div>

                <!-- Private -->
                <div class="fb-config-toggle">
                    <label>{{ $t('form_builder.config.private') }}</label>
                    <input type="checkbox" v-model="field.response_private" :true-value="1" :false-value="0" />
                </div>

                <hr class="fb-config-divider" />

                <!-- Type-specific config -->

                <!-- Placeholder (text, textarea, select) -->
                <div class="fb-config-field" v-if="hasPlaceholder">
                    <label>{{ $t('form_builder.config.placeholder') }}</label>
                    <input
                        type="text"
                        :value="getConfigPlaceholder()"
                        @input="setConfigPlaceholder($event.target.value)"
                        :placeholder="$t('form_builder.config.placeholder')"
                    />
                </div>

                <!-- "As" role (title, content, author_email) -->
                <div class="fb-config-field" v-if="hasAsRole">
                    <label>{{ $t('form_builder.config.as_role') }}</label>
                    <select :value="getConfigAs()" @input="setConfigAs($event.target.value)">
                        <option value="">{{ $t('common.no') }}</option>
                        <option value="title">Titre du ticket</option>
                        <option value="content">Contenu du ticket</option>
                        <option value="author_email">Email de l'auteur</option>
                    </select>
                </div>

                <!-- Options list (select, radio, checkbox) -->
                <div v-if="hasOptions" class="fb-config-field">
                    <label>{{ $t('form_builder.config.choices') }} ({{ activeLang.toUpperCase() }})</label>
                    <div class="fb-config-options">
                        <div
                            v-for="(option, index) in getOptions()"
                            :key="index"
                            class="fb-config-option"
                        >
                            <input
                                type="text"
                                :value="option"
                                @input="updateOption(index, $event.target.value)"
                            />
                            <button class="fb-config-option-delete" @click="removeOption(index)">
                                <i class="fal fa-times"></i>
                            </button>
                        </div>
                        <button class="fb-config-option-add" @click="addOption">
                            <i class="fal fa-plus mr-1"></i> {{ $t('form_builder.config.add_choice') }}
                        </button>
                    </div>
                </div>

                <!-- Category options -->
                <div v-if="isCategory" class="fb-config-field">
                    <label>{{ $t('form_builder.config.categories') }} ({{ activeLang.toUpperCase() }})</label>
                    <div class="fb-config-categories">
                        <div
                            v-for="(cat, index) in getCategoryOptions()"
                            :key="index"
                            class="fb-config-cat-card"
                        >
                            <div class="fb-config-cat-header">
                                <span class="fb-config-cat-num">{{ index + 1 }}</span>
                                <button class="fb-config-option-delete" @click="removeCategoryOption(index)">
                                    <i class="fal fa-times"></i>
                                </button>
                            </div>

                            <!-- Label -->
                            <div class="fb-config-cat-field">
                                <label>{{ $t('form_builder.config.label') }}</label>
                                <input
                                    type="text"
                                    :value="cat.label"
                                    @input="updateCategoryField(index, 'label', $event.target.value)"
                                    :placeholder="$t('form_builder.config.label')"
                                />
                            </div>

                            <!-- Icon -->
                            <div class="fb-config-cat-field">
                                <label>{{ $t('form_builder.config.cat_icon') }}</label>
                                <IconPicker
                                    :value="cat.icon"
                                    :color="cat.color"
                                    @input="updateCategoryField(index, 'icon', $event)"
                                    @update:color="updateCategoryField(index, 'color', $event)"
                                />
                            </div>

                            <!-- Operators -->
                            <div class="fb-config-cat-field">
                                <label>{{ $t('form_builder.config.cat_operators') }}</label>
                                <v-select
                                    :value="parseUserValue(cat.operators)"
                                    :filterable="false"
                                    :options="userOptions"
                                    label="name"
                                    multiple
                                    class="fb-config-vselect"
                                    @search="onUserSearch"
                                    @input="(val) => updateCategoryField(index, 'operators', formatUserValue(val))"
                                >
                                    <template slot="no-options">
                                        {{ $t('post.page.racis.searchuser') }}
                                    </template>
                                </v-select>
                            </div>

                            <!-- Informed -->
                            <div class="fb-config-cat-field">
                                <label>{{ $t('form_builder.config.cat_informed') }}</label>
                                <v-select
                                    :value="parseUserValue(cat.informed)"
                                    :filterable="false"
                                    :options="userOptions"
                                    label="name"
                                    multiple
                                    class="fb-config-vselect"
                                    @search="onUserSearch"
                                    @input="(val) => updateCategoryField(index, 'informed', formatUserValue(val))"
                                >
                                    <template slot="no-options">
                                        {{ $t('post.page.racis.searchuser') }}
                                    </template>
                                </v-select>
                            </div>

                            <!-- Subcategories -->
                            <div class="fb-config-cat-children">
                                <button
                                    class="fb-config-cat-children-toggle"
                                    @click="toggleSubcategories(index)"
                                >
                                    <i class="fal" :class="expandedCats.includes(index) ? 'fa-chevron-down' : 'fa-chevron-right'"></i>
                                    {{ $t('form_builder.config.subcategories') }}
                                    <span class="fb-config-cat-children-count" v-if="cat.children && cat.children.length">
                                        {{ cat.children.length }}
                                    </span>
                                </button>
                                <div v-if="expandedCats.includes(index)" class="fb-config-cat-children-list">
                                    <div
                                        v-for="(sub, subIndex) in (cat.children || [])"
                                        :key="subIndex"
                                        class="fb-config-subcat-card"
                                    >
                                        <div class="fb-config-cat-header">
                                            <span class="fb-config-cat-num fb-config-cat-num--sub">{{ index + 1 }}.{{ subIndex + 1 }}</span>
                                            <button class="fb-config-option-delete" @click="removeSubcategory(index, subIndex)">
                                                <i class="fal fa-times"></i>
                                            </button>
                                        </div>
                                        <div class="fb-config-cat-field">
                                            <label>{{ $t('form_builder.config.label') }}</label>
                                            <input
                                                type="text"
                                                :value="sub.label"
                                                @input="updateSubcategoryField(index, subIndex, 'label', $event.target.value)"
                                                :placeholder="$t('form_builder.config.label')"
                                            />
                                        </div>
                                        <div class="fb-config-cat-field">
                                            <label>{{ $t('form_builder.config.cat_icon') }}</label>
                                            <IconPicker
                                                :value="sub.icon"
                                                :color="sub.color"
                                                @input="updateSubcategoryField(index, subIndex, 'icon', $event)"
                                                @update:color="updateSubcategoryField(index, subIndex, 'color', $event)"
                                            />
                                        </div>
                                    </div>
                                    <button class="fb-config-option-add fb-config-option-add--sub" @click="addSubcategory(index)">
                                        <i class="fal fa-plus mr-1"></i> {{ $t('form_builder.config.add_subcategory') }}
                                    </button>
                                </div>
                            </div>
                        </div>

                        <button class="fb-config-option-add" @click="addCategoryOption">
                            <i class="fal fa-plus mr-1"></i> {{ $t('form_builder.config.add_category') }}
                        </button>
                    </div>
                </div>

                <!-- File upload config -->
                <template v-if="isFileUpload">
                    <div class="fb-config-field">
                        <label>{{ $t('form_builder.config.file_type') }}</label>
                        <select v-model="field.config.type">
                            <option value="files">Documents</option>
                            <option value="pictures">Images</option>
                            <option value="audio">Audio</option>
                            <option value="videos">Vidéos</option>
                        </select>
                    </div>
                    <div class="fb-config-field">
                        <label>{{ $t('form_builder.config.file_max') }}</label>
                        <input type="number" v-model.number="field.config.max" min="1" max="100" />
                        <small style="color: #94a3b8; font-size: 0.72rem;">{{ $t('form_builder.config.file_max_hint') }}</small>
                    </div>
                    <div class="fb-config-field">
                        <label>{{ $t('form_builder.config.file_max_count') }}</label>
                        <input type="number" v-model.number="field.config.max_files" min="1" max="50" />
                        <small style="color: #94a3b8; font-size: 0.72rem;">{{ $t('form_builder.config.file_max_count_hint') }}</small>
                    </div>
                </template>

                <!-- Audio config -->
                <template v-if="isAudio">
                    <div class="fb-config-field">
                        <label>{{ $t('form_builder.config.audio_max_duration') }}</label>
                        <input type="number" v-model.number="field.config.maxDuration" min="5" max="300" />
                        <small style="color: #94a3b8; font-size: 0.72rem;">{{ $t('form_builder.config.audio_seconds') }}</small>
                    </div>
                    <div class="fb-config-field">
                        <label>{{ $t('form_builder.config.audio_format') }}</label>
                        <select v-model="field.config.format">
                            <option value="webm">WebM</option>
                            <option value="mp3">MP3</option>
                            <option value="wav">WAV</option>
                        </select>
                    </div>
                </template>

                <!-- Map config -->
                <template v-if="isMap">
                    <h4 class="fb-config-section-title">{{ $t('form_builder.config.map_center_title') }}</h4>

                    <div class="fb-config-row">
                        <div class="fb-config-field fb-config-field--half">
                            <label>{{ $t('form_builder.config.map_latitude') }}</label>
                            <input type="number" step="any" v-model.number="mapConfig.center[0]" placeholder="48.8566" />
                        </div>
                        <div class="fb-config-field fb-config-field--half">
                            <label>{{ $t('form_builder.config.map_longitude') }}</label>
                            <input type="number" step="any" v-model.number="mapConfig.center[1]" placeholder="2.3522" />
                        </div>
                    </div>

                    <div class="fb-config-row">
                        <div class="fb-config-field fb-config-field--half">
                            <label>{{ $t('form_builder.config.map_zoom') }}</label>
                            <input type="number" v-model.number="mapConfig.zoom" min="1" max="20" />
                        </div>
                        <div class="fb-config-field fb-config-field--half">
                            <label>{{ $t('form_builder.config.map_provider') }}</label>
                            <select v-model="mapConfig.type">
                                <option value="osm">OpenStreetMap</option>
                                <option value="bing">Bing Maps</option>
                                <option value="maps">Google Maps</option>
                                <option value="banoc">BANOC</option>
                            </select>
                        </div>
                    </div>

                    <div class="fb-config-field">
                        <label>{{ $t('form_builder.config.map_style') }}</label>
                        <select v-model="mapConfig.carte">
                            <option value="city">{{ $t('form_builder.config.map_style_city') }}</option>
                            <option value="satellite">{{ $t('form_builder.config.map_style_satellite') }}</option>
                        </select>
                    </div>

                    <hr class="fb-config-divider" />
                    <h4 class="fb-config-section-title">{{ $t('form_builder.config.map_restriction_title') }}</h4>

                    <div class="fb-config-field">
                        <label>{{ $t('form_builder.config.map_city') }}</label>
                        <input type="text" v-model="mapRestriction.city" :placeholder="$t('form_builder.config.map_city')" />
                    </div>
                    <div class="fb-config-field">
                        <label>{{ $t('form_builder.config.map_country') }}</label>
                        <input type="text" v-model="mapRestriction.country" :placeholder="$t('form_builder.config.map_country_placeholder')" />
                    </div>
                </template>
            </div>
        </template>
    </div>
</template>

<script>
const FIELD_ICONS = {
    1: 'fal fa-font', 2: 'fal fa-eye-slash', 3: 'fal fa-align-left', 4: 'fal fa-paragraph',
    5: 'fal fa-hashtag', 6: 'fal fa-map-marker-alt', 7: 'fal fa-calendar', 8: 'fal fa-calendar-alt',
    9: 'fal fa-list-alt', 10: 'fal fa-dot-circle', 11: 'fal fa-check-square', 12: 'fal fa-cloud-upload',
    13: 'fal fa-folder-tree', 14: 'fal fa-shield-alt', 15: 'fal fa-database', 16: 'fal fa-envelope', 17: 'fal fa-star',
    18: 'fal fa-microphone'
}

const FIELD_TYPE_NAMES = {
    1: 'Texte court', 2: 'Caché', 3: 'Texte long', 4: 'Texte riche',
    5: 'Nombre', 6: 'Localisation', 7: 'Date', 8: 'Date & heure',
    9: 'Liste déroulante', 10: 'Radio', 11: 'Checkbox', 12: 'Fichier',
    13: 'Catégorie', 14: 'RGPD', 15: 'Requête', 16: 'Email', 17: 'Notation',
    18: 'Prise audio'
}

const FILE_TYPE_ICONS = {
    pictures: 'fal fa-image',
    audio: 'fal fa-headphones',
    videos: 'fal fa-video',
    files: 'fal fa-file-pdf'
}

const FILE_TYPE_NAMES = {
    pictures: 'Images',
    audio: 'Audio',
    videos: 'Vidéos',
    files: 'Documents'
}

import { mapActions } from 'vuex'
import IconPicker from '@/components/UI/IconPicker.vue'

export default {
    name: 'FormBuilderFieldConfig',
    components: { IconPicker },
    props: {
        field: {
            type: Object,
            default: null
        },
        knownUsers: {
            type: Array,
            default: () => []
        }
    },
    data() {
        return {
            activeLang: 'fr',
            langs: ['fr', 'en'],
            userOptions: [],
            expandedCats: []
        }
    },
    computed: {
        typeId() {
            return this.field ? (this.field.form_attribute_type_id || this.field.typeId) : null
        },
        hasPlaceholder() {
            return [1, 3, 4, 5, 9, 16].includes(this.typeId)
        },
        hasAsRole() {
            return [1, 4, 16].includes(this.typeId)
        },
        hasOptions() {
            return [9, 10, 11].includes(this.typeId)
        },
        isFileUpload() {
            return this.typeId === 12
        },
        isMap() {
            return this.typeId === 6
        },
        isAudio() {
            return this.typeId === 18
        },
        isCategory() {
            return this.typeId === 13
        },
        mapConfig() {
            if (!this.field.config.map) {
                // eslint-disable-next-line vue/no-side-effects-in-computed-properties
                this.$set(this.field.config, 'map', {
                    config: {
                        center: [48.8566, 2.3522],
                        zoom: 13,
                        type: 'osm',
                        carte: 'city',
                        restriction: { city: '', country: '' }
                    },
                    pois: [],
                    restrict: { cities: [] }
                })
            }
            if (!this.field.config.map.config.type) {
                // eslint-disable-next-line vue/no-side-effects-in-computed-properties
                this.$set(this.field.config.map.config, 'type', 'osm')
            }
            if (!this.field.config.map.config.restriction) {
                // eslint-disable-next-line vue/no-side-effects-in-computed-properties
                this.$set(this.field.config.map.config, 'restriction', { city: '', country: '' })
            }
            return this.field.config.map.config
        },
        mapRestriction() {
            return this.mapConfig.restriction
        }
    },
    methods: {
        getFieldIcon() {
            if (this.typeId === 12 && this.field.config && this.field.config.type) {
                return FILE_TYPE_ICONS[this.field.config.type] || FIELD_ICONS[12]
            }
            return FIELD_ICONS[this.typeId] || 'fal fa-question'
        },
        getFieldTypeName() {
            if (this.typeId === 12 && this.field.config && this.field.config.type) {
                return FILE_TYPE_NAMES[this.field.config.type] || FIELD_TYPE_NAMES[12]
            }
            return FIELD_TYPE_NAMES[this.typeId] || 'Inconnu'
        },
        getTranslation(key) {
            if (!this.field.translations) return ''
            if (Array.isArray(this.field.translations)) {
                const t = this.field.translations.find((t) => t.locale === this.activeLang)
                return t ? t[key] || '' : ''
            }
            return this.field.translations[this.activeLang] ? this.field.translations[this.activeLang][key] || '' : ''
        },
        setTranslation(key, value) {
            if (!this.field.translations) {
                this.$set(this.field, 'translations', {})
            }
            if (Array.isArray(this.field.translations)) {
                let t = this.field.translations.find((t) => t.locale === this.activeLang)
                if (!t) {
                    t = { locale: this.activeLang, label: '', instruction: '', description: '', help: '' }
                    this.field.translations.push(t)
                }
                this.$set(t, key, value)
            } else {
                if (!this.field.translations[this.activeLang]) {
                    this.$set(this.field.translations, this.activeLang, { label: '', instruction: '', description: '', help: '' })
                }
                this.$set(this.field.translations[this.activeLang], key, value)
            }
            if (key === 'label' && this.activeLang === 'fr') {
                this.field.label = value
            }
        },
        getOptions() {
            if (!this.field.config || !this.field.config.options) return []
            return this.field.config.options[this.activeLang] || []
        },
        updateOption(index, value) {
            if (!this.field.config.options[this.activeLang]) return
            this.$set(this.field.config.options[this.activeLang], index, value)
        },
        addOption() {
            if (!this.field.config) this.$set(this.field, 'config', {})
            if (!this.field.config.options) this.$set(this.field.config, 'options', { fr: [], en: [] })
            if (!this.field.config.options[this.activeLang]) this.$set(this.field.config.options, this.activeLang, [])
            this.field.config.options[this.activeLang].push('')
        },
        removeOption(index) {
            this.field.config.options[this.activeLang].splice(index, 1)
        },
        // Category methods
        getCategoryOptions() {
            if (!this.field.config || !this.field.config.options) return []
            return this.field.config.options[this.activeLang] || []
        },
        updateCategoryField(index, key, value) {
            const options = this.field.config.options[this.activeLang]
            if (!options || !options[index]) return
            this.$set(options[index], key, value)
        },
        addCategoryOption() {
            if (!this.field.config) this.$set(this.field, 'config', {})
            if (!this.field.config.options) this.$set(this.field.config, 'options', { fr: [], en: [] })
            if (!this.field.config.options[this.activeLang]) this.$set(this.field.config.options, this.activeLang, [])
            this.field.config.options[this.activeLang].push({
                label: '',
                icon: 'fal fa-folder',
                color: '',
                operators: null,
                informed: null
            })
        },
        removeCategoryOption(index) {
            this.field.config.options[this.activeLang].splice(index, 1)
        },
        // Subcategory methods
        toggleSubcategories(index) {
            const i = this.expandedCats.indexOf(index)
            if (i === -1) {
                this.expandedCats.push(index)
            } else {
                this.expandedCats.splice(i, 1)
            }
        },
        addSubcategory(catIndex) {
            const options = this.field.config.options[this.activeLang]
            if (!options || !options[catIndex]) return
            if (!options[catIndex].children) {
                this.$set(options[catIndex], 'children', [])
            }
            options[catIndex].children.push({
                label: '',
                icon: 'fal fa-folder',
                color: ''
            })
        },
        removeSubcategory(catIndex, subIndex) {
            const options = this.field.config.options[this.activeLang]
            if (!options || !options[catIndex] || !options[catIndex].children) return
            options[catIndex].children.splice(subIndex, 1)
        },
        updateSubcategoryField(catIndex, subIndex, key, value) {
            const options = this.field.config.options[this.activeLang]
            if (!options || !options[catIndex] || !options[catIndex].children || !options[catIndex].children[subIndex]) return
            this.$set(options[catIndex].children[subIndex], key, value)
        },
        getConfigPlaceholder() {
            if (!this.field.config || !this.field.config.placeholder) return ''
            const p = this.field.config.placeholder[this.activeLang]
            return Array.isArray(p) ? p[0] || '' : p || ''
        },
        setConfigPlaceholder(value) {
            if (!this.field.config) this.$set(this.field, 'config', {})
            if (!this.field.config.placeholder) this.$set(this.field.config, 'placeholder', { fr: [], en: [] })
            this.$set(this.field.config.placeholder, this.activeLang, [value])
        },
        getConfigAs() {
            return this.field.config && this.field.config.as ? this.field.config.as : ''
        },
        setConfigAs(value) {
            if (!this.field.config) this.$set(this.field, 'config', {})
            this.$set(this.field.config, 'as', value || [])
        },
        // User search for category operators/informed
        ...mapActions('auth', {
            searchUser: 'searchUser'
        }),
        onUserSearch(search, loading) {
            if (search.length) {
                loading(true)
                this.searchUser(search).then((response) => {
                    this.userOptions = response.data.users.data
                    loading(false)
                })
            }
        },
        parseUserValue(val) {
            if (!val) return []
            if (Array.isArray(val)) return val
            if (typeof val === 'string') {
                return val.split(',').filter(Boolean).map((id) => {
                    const numId = parseInt(id.trim())
                    const found = this.knownUsers.find((u) => u.id === numId) ||
                                  this.userOptions.find((u) => u.id === numId)
                    return found || { id: numId, name: `#${numId}` }
                })
            }
            return []
        },
        formatUserValue(users) {
            if (!users || !users.length) return null
            return users.map((u) => u.id).join(',')
        }
    }
}
</script>

<style scoped>
.fb-config {
    width: 330px;
    background: #fff;
    border-left: 1px solid #e5e7eb;
    display: flex;
    flex-direction: column;
    height: 100%;
    flex-shrink: 0;
    overflow-y: auto;
}

/* -- Empty state -- */
.fb-config-empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    height: 100%;
    padding: 32px;
}

.fb-config-empty-icon {
    width: 56px;
    height: 56px;
    border-radius: 50%;
    background: #f8fafc;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 16px;
}

.fb-config-empty-icon i {
    font-size: 1.4rem;
    color: #cbd5e1;
}

.fb-config-empty-text {
    font-size: 0.82rem;
    color: #94a3b8;
    text-align: center;
    line-height: 1.5;
    max-width: 200px;
}

/* -- Header -- */
.fb-config-header {
    padding: 18px 20px;
    border-bottom: 1px solid #f1f5f9;
}

.fb-config-title {
    font-size: 0.95rem;
    font-weight: 700;
    color: #0f172a;
    margin: 0;
    display: flex;
    align-items: center;
}

.fb-config-title i {
    color: var(--theme-primary, #3b82f6);
}

/* -- Body -- */
.fb-config-body {
    padding: 20px;
}

/* -- Tabs -- */
.fb-config-tabs {
    display: flex;
    gap: 4px;
    margin-bottom: 20px;
    background: #f8fafc;
    border: 1px solid #f1f5f9;
    border-radius: 10px;
    padding: 3px;
}

.fb-config-tab {
    flex: 1;
    padding: 7px;
    border: none;
    background: transparent;
    font-size: 0.78rem;
    font-weight: 600;
    color: #94a3b8;
    border-radius: 8px;
    cursor: pointer;
    transition: all 0.2s ease;
}

.fb-config-tab:hover:not(.active) {
    color: #64748b;
}

.fb-config-tab.active {
    background: #fff;
    color: var(--theme-primary, #3b82f6);
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06), 0 0 0 1px rgba(0, 0, 0, 0.03);
}

/* -- Fields -- */
.fb-config-field {
    margin-bottom: 16px;
}

.fb-config-field label {
    display: block;
    font-size: 0.76rem;
    font-weight: 600;
    color: #475569;
    margin-bottom: 6px;
    letter-spacing: 0.01em;
}

.fb-config-field input,
.fb-config-field textarea,
.fb-config-field select {
    width: 100%;
    padding: 9px 12px;
    border: 1.5px solid #e2e8f0;
    border-radius: 9px;
    font-size: 0.84rem;
    color: #0f172a;
    outline: none;
    box-sizing: border-box;
    background: #fff;
    transition: border-color 0.2s ease, box-shadow 0.2s ease;
}

.fb-config-field input::placeholder,
.fb-config-field textarea::placeholder {
    color: #cbd5e1;
}

.fb-config-field input:hover,
.fb-config-field textarea:hover,
.fb-config-field select:hover {
    border-color: #cbd5e1;
}

.fb-config-field input:focus,
.fb-config-field textarea:focus,
.fb-config-field select:focus {
    border-color: var(--theme-primary, #3b82f6) !important;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.08) !important;
}

.fb-config-field textarea {
    resize: vertical;
    min-height: 60px;
}

/* -- Toggles -- */
.fb-config-toggle {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 12px;
    padding: 6px 0;
}

.fb-config-toggle label {
    font-size: 0.82rem;
    color: #475569;
    margin: 0;
    font-weight: 500;
}

.fb-config-toggle input[type='checkbox'] {
    width: 18px;
    height: 18px;
    accent-color: var(--theme-primary, #3b82f6);
    cursor: pointer;
    border-radius: 4px;
}

/* -- Divider -- */
.fb-config-divider {
    border: none;
    border-top: 1px solid #f1f5f9;
    margin: 18px 0;
}

/* -- Section title -- */
.fb-config-section-title {
    font-size: 0.72rem;
    font-weight: 700;
    color: #94a3b8;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    margin-bottom: 14px;
}

/* -- Options list -- */
.fb-config-options {
    display: flex;
    flex-direction: column;
    gap: 7px;
}

.fb-config-option {
    display: flex;
    gap: 6px;
}

.fb-config-option input {
    flex: 1;
    padding: 7px 10px;
    border: 1.5px solid #e2e8f0;
    border-radius: 8px;
    font-size: 0.82rem;
    outline: none;
    box-sizing: border-box;
    transition: border-color 0.2s ease;
}

.fb-config-option input:focus {
    border-color: var(--theme-primary, #3b82f6) !important;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.08) !important;
}

.fb-config-option-delete {
    border: none;
    background: none;
    color: #e2e8f0;
    cursor: pointer;
    padding: 4px;
    border-radius: 6px;
    transition: all 0.15s;
}

.fb-config-option-delete:hover {
    color: #ef4444;
    background: #fef2f2;
}

.fb-config-option-add {
    border: 1.5px dashed #d1d5db;
    background: transparent;
    border-radius: 8px;
    padding: 8px;
    font-size: 0.78rem;
    font-weight: 500;
    color: #64748b;
    cursor: pointer;
    transition: all 0.2s ease;
}

.fb-config-option-add:hover {
    border-color: var(--theme-primary, #3b82f6);
    color: var(--theme-primary, #3b82f6);
    background: #eff6ff;
}

/* -- Category cards -- */
.fb-config-categories {
    display: flex;
    flex-direction: column;
    gap: 12px;
}

.fb-config-cat-card {
    border: 1.5px solid #f1f5f9;
    border-radius: 10px;
    padding: 12px;
    background: #fafbfc;
    transition: border-color 0.2s ease;
}

.fb-config-cat-card:hover {
    border-color: #e2e8f0;
}

.fb-config-cat-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 10px;
}

.fb-config-cat-num {
    width: 22px;
    height: 22px;
    border-radius: 6px;
    background: var(--theme-primary, #3b82f6);
    color: #fff;
    font-size: 0.68rem;
    font-weight: 700;
    display: flex;
    align-items: center;
    justify-content: center;
}

.fb-config-cat-field {
    margin-bottom: 8px;
}

.fb-config-cat-field label {
    display: block;
    font-size: 0.7rem;
    font-weight: 600;
    color: #64748b;
    margin-bottom: 3px;
}

.fb-config-cat-field input {
    width: 100%;
    padding: 6px 10px;
    border: 1.5px solid #e2e8f0;
    border-radius: 7px;
    font-size: 0.8rem;
    color: #0f172a;
    outline: none;
    box-sizing: border-box;
    transition: border-color 0.2s ease;
}

.fb-config-cat-field input:focus {
    border-color: var(--theme-primary, #3b82f6) !important;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.08) !important;
}

/* v-select in category cards */
.fb-config-vselect .vs__dropdown-toggle {
    border: 1.5px solid #e2e8f0;
    border-radius: 7px;
    padding: 2px 5px;
    min-height: 32px;
    background: #fff;
    font-size: 0.8rem;
}

.fb-config-vselect .vs__dropdown-toggle:focus-within {
    border-color: var(--theme-primary, #3b82f6) !important;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.08) !important;
}

.fb-config-vselect .vs__selected {
    background: #eff6ff;
    border: 1px solid #bfdbfe;
    border-radius: 5px;
    color: #1e40af;
    font-size: 0.72rem;
    padding: 1px 6px;
    margin: 1px;
}

.fb-config-vselect .vs__deselect {
    color: #93c5fd;
}

.fb-config-vselect .vs__search {
    font-size: 0.78rem;
}

.fb-config-vselect .vs__search::placeholder {
    color: #cbd5e1;
}

/* -- Row layout -- */
.fb-config-row {
    display: flex;
    gap: 10px;
    margin-bottom: 16px;
}

.fb-config-row .fb-config-field {
    margin-bottom: 0;
}

.fb-config-field--half {
    flex: 1;
}

/* -- Hint text -- */
.fb-config-hint {
    font-size: 0.72rem;
    color: #94a3b8;
    margin-top: 4px;
}

/* -- Subcategories -- */
.fb-config-cat-children {
    margin-top: 8px;
    border-top: 1px solid #f1f5f9;
    padding-top: 8px;
}

.fb-config-cat-children-toggle {
    display: flex;
    align-items: center;
    gap: 6px;
    width: 100%;
    border: none;
    background: none;
    color: #64748b;
    font-size: 0.75rem;
    font-weight: 600;
    cursor: pointer;
    padding: 4px 0;
    transition: color 0.2s;
}

.fb-config-cat-children-toggle:hover {
    color: #0891B2;
}

.fb-config-cat-children-toggle i {
    font-size: 0.65rem;
    width: 12px;
    text-align: center;
}

.fb-config-cat-children-count {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 18px;
    height: 18px;
    padding: 0 5px;
    border-radius: 10px;
    background: #0891B2;
    color: #fff;
    font-size: 0.65rem;
    font-weight: 700;
}

.fb-config-cat-children-list {
    margin-top: 8px;
    padding-left: 10px;
    border-left: 2px solid #e2e8f0;
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.fb-config-subcat-card {
    border: 1.5px solid #e2e8f0;
    border-radius: 8px;
    padding: 10px;
    background: #fff;
}

.fb-config-cat-num--sub {
    background: #64748b;
    font-size: 0.6rem;
    width: auto;
    padding: 0 6px;
}

.fb-config-option-add--sub {
    font-size: 0.72rem;
    padding: 6px;
}
</style>
